import React, { useState, useEffect, useCallback } from 'react';
import axios from '../../utils/axiosConfig';
import debounce from 'lodash.debounce';
import { setAuthToken } from '../../utils/auth';
import { InfoTooltip } from '../Common/InfoTooltip';
import { useNavigate } from 'react-router-dom';
import '../../styles/ReviewManagement.css';

const ReviewManagement = () => {
    const [activeSection, setActiveSection] = useState('configuration');
    const [config, setConfig] = useState({
        questions: [],
        notification_emails: []
    });
    const [reviews, setReviews] = useState([]);
    const [stats, setStats] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saveStatus, setSaveStatus] = useState({ show: false, message: '', type: '' });
    const [ccEmailInput, setCcEmailInput] = useState('');
    const [showGettingStarted, setShowGettingStarted] = useState(true);
    const [vendorData, setVendorData] = useState(null);
    const navigate = useNavigate();
    const [openMenuId, setOpenMenuId] = useState(null);
    
    // Determine if this is the user's first time setting up reviews
    const isFirstTimeSetup = !isLoading && config.questions.length === 0;

    const fetchVendorData = async () => {
        try {
            const [configRes, reviewsRes, statsRes, profileRes] = await Promise.all([
                axios.get('/api/reviews/vendor/config'),
                axios.get('/api/reviews/vendor/reviews'),   
                axios.get('/api/reviews/vendor/stats'),
                axios.get('/api/vendors/profile')      
            ]);
            
            // Process the stats data to ensure numeric values
            const processedStats = {
                ...statsRes.data,
                total_reviews: Number(statsRes.data.total_reviews || 0),
                average_rating: Number(statsRes.data.average_rating || 0),
                last_30_days: Number(statsRes.data.last_30_days || 0),
                questionStats: statsRes.data.questionStats?.map(stat => ({
                    ...stat,
                    average_rating: Number(stat.average_rating || 0),
                    response_count: Number(stat.response_count || 0),
                    rating_distribution: stat.rating_distribution ? 
                        Object.fromEntries(
                            Object.entries(stat.rating_distribution)
                                .map(([key, value]) => [key, Number(value || 0)])
                        ) : {}
                })) || []
            };
            
            setConfig(configRes.data);
            setReviews(reviewsRes.data?.reviews || []);
            setStats(processedStats);
            setVendorData(profileRes.data);
            setIsLoading(false);
       
                          
         // Hide getting started section if they already have configurations
                if (configRes.data.questions.length > 0) {
                    setShowGettingStarted(false);
                }												  
                
        
        } catch (err) {
            console.error('Error fetching review data:', err?.response?.data || err.message);
            setError('Error loading review data');
            setIsLoading(false);
        }
    };

    const debouncedFetchData = useCallback(
        debounce(fetchVendorData, 300),
        [] // Empty dependency array since fetchVendorData doesn't depend on any props or state
    );

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            setAuthToken(token);
        }
        debouncedFetchData();
        return () => debouncedFetchData.cancel();
    }, [debouncedFetchData]);

    // closing menus when clicking elsewhere
        useEffect(() => {
            const handleClickOutside = (event) => {
            if (openMenuId && !event.target.closest('.rm-menu-dropdown')) {
                setOpenMenuId(null);
            }
            };
            
            document.addEventListener('mousedown', handleClickOutside);
            return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            };
        }, [openMenuId]);

    const addQuestion = () => {
        // Generate a smaller, safe integer ID
        const newId = Math.floor(Math.random() * 1000000); // This will generate IDs between 0 and 999999
        
        setConfig(prev => ({
            ...prev,
            questions: [
                ...prev.questions,
                {
                    id: newId,
                    type: 'star_rating',
                    text: '',
                    required: true,
                    sort_order: prev.questions.length
                }
            ]
        }));
        
        // Hide getting started when they add their first question
        setShowGettingStarted(false);
    };

    const updateQuestion = (index, field, value) => {
        const updatedQuestions = [...config.questions];
        updatedQuestions[index] = {
            ...updatedQuestions[index],
            [field]: value
        };
        setConfig(prev => ({ ...prev, questions: updatedQuestions }));
    };

    const saveConfig = async () => {
        try {
            // Ensure questions have sort_order
            const questionsWithOrder = config.questions.map((q, index) => ({
                ...q,
                sort_order: index
            }));
            
            // Format notification emails for backend
            let notificationEmails = config.notification_emails;
            if (typeof notificationEmails === 'string') {
                notificationEmails = notificationEmails.split(',').map(email => email.trim()).filter(email => email);
            }

            await axios.put('/api/reviews/vendor/config', {
                ...config,
                questions: questionsWithOrder,
                notification_emails: notificationEmails
            });
            
            setSaveStatus({
                show: true,
                message: 'Configuration saved successfully',
                type: 'success'
            });
            setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
        } catch (err) {
            setSaveStatus({
                show: true,
                message: 'Error saving configuration',
                type: 'error'
            });
        }
    };


 // Modified moveQuestion function with mobile support
const moveQuestion = (fromIndex, toIndex) => {
    // Validate indices to prevent errors
    if (toIndex < 0 || toIndex >= config.questions.length) return;
  
    const updatedQuestions = [...config.questions];
    const [movedItem] = updatedQuestions.splice(fromIndex, 1);
    updatedQuestions.splice(toIndex, 0, movedItem);
    
    // Update sort_order values
    const reorderedQuestions = updatedQuestions.map((q, idx) => ({
      ...q,
      sort_order: idx
    }));
    
    setConfig(prev => ({ ...prev, questions: reorderedQuestions }));
  };

    // Question deletion
// Modified deleteQuestion function that auto-saves after deletion
const deleteQuestion = (index) => {
    setConfirmDialog({
      show: true,
      message: 'Are you sure you want to delete this question? This action cannot be undone.',
      onConfirm: async () => {
        const updatedQuestions = config.questions.filter((_, i) => i !== index);
        
        // Update sort_order values after deletion
        const reorderedQuestions = updatedQuestions.map((q, idx) => ({
          ...q,
          sort_order: idx
        }));
        
        // Set the state first
        setConfig(prev => ({ ...prev, questions: reorderedQuestions }));
        
        // Close the dialog
        setConfirmDialog({ show: false });
        
        // Auto-save the configuration
        try {
          await axios.put('/api/reviews/vendor/config', {
            ...config,
            questions: reorderedQuestions
          });
          
          // Show success message
          setSaveStatus({
            show: true,
            message: 'Question deleted and changes saved',
            type: 'success'
          });
          setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
        } catch (err) {
          setSaveStatus({
            show: true,
            message: 'Question deleted but failed to save changes',
            type: 'error'
          });
          setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
        }
      },
      onCancel: () => {
        setConfirmDialog({ show: false });
      }
    });
  };

    const [confirmDialog, setConfirmDialog] = useState({
                show: false,
                message: '',
                onConfirm: null,
                onCancel: null
                });

        const renderConfirmDialog = () => (
            confirmDialog.show && (
                <div className="rm-confirm-dialog-overlay">
                    <div className="rm-confirm-dialog">
                        <p>{confirmDialog.message}</p>
                        <div className="rm-confirm-actions">
                            <button 
                                className="rm-button secondary" 
                                onClick={confirmDialog.onCancel}
                            >
                                Cancel
                            </button>
                            <button 
                                className="rm-button primary" 
                                onClick={confirmDialog.onConfirm}
                            >
                                Confirm
                            </button>
                        </div>
                    </div>
                </div>
            )
        );
    
    // Handle adding notification emails
    const handleAddEmail = () => {
        if (!ccEmailInput.trim()) return;
        
        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(ccEmailInput.trim())) {
            setSaveStatus({
                show: true,
                message: 'Please enter a valid email address',
                type: 'error'
            });
            setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
            return;
        }
        
        // Add email to the list
        const newEmails = Array.isArray(config.notification_emails) 
            ? [...config.notification_emails, ccEmailInput.trim()]
            : [ccEmailInput.trim()];
        
        setConfig(prev => ({
            ...prev,
            notification_emails: newEmails
        }));
        
        setCcEmailInput('');
    };
    
    // Handle removing notification emails
    const handleRemoveEmail = (emailToRemove) => {
        const updatedEmails = Array.isArray(config.notification_emails)
            ? config.notification_emails.filter(email => email !== emailToRemove)
            : [];
        
        setConfig(prev => ({
            ...prev,
            notification_emails: updatedEmails
        }));
    };
    
    // Handle Enter key for email input
    const handleEmailKeyDown = (e) => {
        if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            handleAddEmail();
        }
    };
    
    // Preview link generation
    const getReviewLink = () => {
        if (!vendorData || !vendorData.sub_url) return '';
        
        const baseUrl = process.env.NODE_ENV === 'development' 
            ? window.location.origin 
            : (process.env.REACT_APP_FRONTEND_URL || window.location.origin);
            
        return `${baseUrl}/vendor/${vendorData.sub_url}/review`;
    };
    

    // Copy review link to clipboard
    const copyReviewLink = () => {
        const link = getReviewLink();
        navigator.clipboard.writeText(link)
            .then(() => {
                setSaveStatus({
                    show: true,
                    message: 'Review link copied to clipboard',
                    type: 'success'
                });
                setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
            })
            .catch(err => {
                setSaveStatus({
                    show: true,
                    message: 'Failed to copy link',
                    type: 'error'
                });
                setTimeout(() => setSaveStatus({ show: false, message: '', type: '' }), 3000);
            });
    };
    
    
    // Navigate to review page
    const goToReviewPage = () => {
        const link = getReviewLink();
        window.open(link, '_blank');
    };
    
    const renderGettingStarted = () => (
        <div className="rm-getting-started-card">
            <div className="rm-getting-started-header">
                <h3>Get Started with Reviews</h3>
                <button 
                    onClick={() => setShowGettingStarted(false)} 
                    className="rm-button-close"
                    aria-label="Close getting started guide"
                >
                    &times;
                </button>
            </div>
            <div className="rm-getting-started-content">
                <div className="rm-getting-started-step">
                    <div className="rm-step-number">1</div>
                    <div className="rm-step-content">
                        <h4>Create your review questions</h4>
                        <p>Add star rating questions for quantitative feedback or text questions for qualitative insights.</p>
                    </div>
                </div>
                
                <div className="rm-getting-started-step">
                    <div className="rm-step-number">2</div>
                    <div className="rm-step-content">
                        <h4>Set up notification emails</h4>
                        <p>Receive alerts when customers submit new reviews.</p>
                    </div>
                </div>
                
                <div className="rm-getting-started-step">
                    <div className="rm-step-number">3</div>
                    <div className="rm-step-content">
                        <h4>Share your review link</h4>
                        <p>Send customers to your review page after their experience.</p>
                        <div className="rm-review-link-actions">
                            <div className="rm-review-link">{getReviewLink()}</div>
                            <button onClick={copyReviewLink} className="rm-button secondary small">
                                Copy Link
                            </button>
                            <button onClick={goToReviewPage} className="rm-button primary small">
                                View Page
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderConfiguration = () => (
        <div className="rm-configuration">
            {showGettingStarted && isFirstTimeSetup && renderGettingStarted()}
            
   
            
            <div className="rm-card rm-question-config-card">
                <div className="rm-card-header">
                    <h3>
                        Review Questions
                        <InfoTooltip 
                            text="Configure the questions customers will answer in your review form" 
                            variant="vendor"
                        />
                    </h3>
                    <div className="rm-card-actions">
                        <button onClick={addQuestion} className="rm-button primary">
                            Add Question
                        </button>
                    </div>
                </div>
                
                <div className="rm-card-body">
                    {config.questions.length === 0 ? (
                        <div className="rm-empty-state">
                            <div className="rm-empty-icon">★</div>
                            <h4>No review questions yet</h4>
                            <p>Start by adding your first question for customers to answer</p>
                            <button onClick={addQuestion} className="rm-button primary">
                                Add First Question
                            </button>
                        </div>
                    ) : (
                        <div className="rm-question-list">
                            <div className="rm-question-list-header">
                                <div className="rm-question-list-header-order">Order</div>
                                <div className="rm-question-list-header-type">Type</div>
                                <div className="rm-question-list-header-text">Question</div>
                                <div className="rm-question-list-header-required">Required</div>
                                <div className="rm-question-list-header-actions">Actions</div>
                            </div>
                            
                            {config.questions.map((question, index) => (
                                <div key={question.id} className="rm-question-item">
                                    <div className="rm-question-order">
                                        <div className="rm-order-controls">
                                            <button 
                                                className="rm-order-btn"
                                                onClick={() => moveQuestion(index, index - 1)}
                                                disabled={index === 0}
                                                aria-label="Move question up"
                                            >
                                                ↑
                                            </button>
                                            <span className="rm-order-number">{index + 1}</span>
                                            <button 
                                                className="rm-order-btn"
                                                onClick={() => moveQuestion(index, index + 1)}
                                                disabled={index === config.questions.length - 1}
                                                aria-label="Move question down"
                                            >
                                                ↓
                                            </button>
                                        </div>
                                    </div>
                                    
                                    <div className="rm-question-type">
                                        <select
                                            value={question.type}
                                            onChange={(e) => updateQuestion(index, 'type', e.target.value)}
                                            className="rm-select"
                                        >
                                            <option value="star_rating">Star Rating</option>
                                            <option value="text">Text Response</option>
                                        </select>
                                    </div>
                                    
                                    <div className="rm-question-text">
                                        <input
                                            type="text"
                                            placeholder="Enter your question here"
                                            value={question.text}
                                            onChange={(e) => updateQuestion(index, 'text', e.target.value)}
                                            className="rm-input"
                                        />
                                    </div>
                                    
                                    <div className="rm-question-required">
                                        <label className="rm-switch">
                                            <input
                                                type="checkbox"
                                                checked={question.required}
                                                onChange={(e) => updateQuestion(index, 'required', e.target.checked)}
                                            />
                                            <span className="rm-slider"></span>
                                        </label>
                                    </div>
                                    
                               
                                    <div className="rm-question-actions">
                                    <div className="rm-menu-dropdown">
                                        <button 
                                        className="rm-button-icon" 
                                        aria-label="More options"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Close any other open menus first
                                            setOpenMenuId(openMenuId === question.id ? null : question.id);
                                        }}
                                        >
                                        •••
                                        </button>
                                        {openMenuId === question.id && (
                                        <div className="rm-menu-content">
                                            <button 
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setOpenMenuId(null); // Close menu first
                                                deleteQuestion(index);
                                            }}
                                            className="rm-menu-item danger"
                                            >
                                            Delete
                                            </button>
                                        </div>
                                        )}
                                    </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    
                    <div className="rm-question-preview">
                        <h4>Question Preview</h4>
                        <div className="rm-preview-container">
                            {config.questions.length > 0 ? (
                                <div className="rm-preview-questions">
                                    {config.questions.map((question, idx) => (
                                        <div key={`preview-${question.id}`} className="rm-preview-question">
                                            <label className="rm-preview-label">
                                                {question.text || `Question ${idx + 1}`}
                                                {question.required && <span className="rm-required">*</span>}
                                            </label>
                                            {question.type === 'star_rating' ? (
                                                <div className="rm-preview-stars">
                                                    {'★★★★★'.split('').map((star, i) => (
                                                        <span key={i} className="rm-preview-star">★</span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="rm-preview-text">
                                                    <div className="rm-preview-textarea"></div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="rm-preview-empty">
                                    <p>Add questions to see how they'll appear to customers</p>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="rm-form-actions">
                        <button onClick={saveConfig} className="rm-button primary large">
                            Save Questions
                        </button>
                    </div>

                </div>

                  
            </div>
            
            <div className="rm-card rm-notification-settings-card">
                <div className="rm-card-header">
                    <h3>
                        Notification Settings
                        <InfoTooltip 
                            text="Receive email alerts when customers submit reviews" 
                            variant="vendor"
                        />
                    </h3>
                </div>
                
                <div className="rm-card-body">
                    <div className="rm-form-group">
                        <label htmlFor="notificationEmails">
                            Notification Emails
                            <InfoTooltip 
                                text="Add email addresses to receive notifications when new reviews are submitted" 
                                variant="vendor"
                            />
                        </label>
                        <div className="rm-notification-emails">
                            <div className="rm-email-input-container">
                                <input
                                    type="email"
                                    id="notificationEmails"
                                    value={ccEmailInput}
                                    onChange={(e) => setCcEmailInput(e.target.value)}
                                    onKeyDown={handleEmailKeyDown}
                                    className="rm-input"
                                    placeholder="Enter email and press Enter"
                                />
                                <button 
                                    onClick={handleAddEmail} 
                                    className="rm-button secondary small"
                                    disabled={!ccEmailInput.trim()}
                                >
                                    Add
                                </button>
                            </div>
                            
                            {Array.isArray(config.notification_emails) && config.notification_emails.length > 0 && (
                                <div className="rm-email-list">
                                    {config.notification_emails.map((email, idx) => (
                                        <div key={`email-${idx}`} className="rm-email-tag">
                                            <span>{email}</span>
                                            <button 
                                                onClick={() => handleRemoveEmail(email)}
                                                className="rm-remove-email"
                                                aria-label={`Remove ${email}`}
                                            >
                                                &times;
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="rm-form-actions">
                        <button onClick={saveConfig} className="rm-button primary large">
                            Save Configuration
                        </button>
                    </div>


                    </div>
                    
                    <div className="rm-share-review-section">
                        <h4>Share Your Review Page</h4>
                        <p>Send customers to your review page after their experience.</p>
                        <div className="rm-review-link-actions">
                            <div className="rm-review-link">{getReviewLink()}</div>
                            <button onClick={copyReviewLink} className="rm-button secondary">
                                Copy Link
                            </button>
                            <button onClick={goToReviewPage} className="rm-button primary">
                                View Page
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            
          
        </div>
    );

    const renderReviews = () => (
        <div className="rm-reviews-container">
            <div className="rm-stats-grid">
                <div className="rm-stat-card">
                    <div className="rm-stat-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                        </svg>
                    </div>
                    <div className="rm-stat-content">
                        <h4>Total Reviews</h4>
                        <p className="rm-stat-value">{stats?.total_reviews || 0}</p>
                    </div>
                </div>
                
                <div className="rm-stat-card">
                    <div className="rm-stat-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                    <div className="rm-stat-content">
                        <h4>Average Rating</h4>
                        <p className="rm-stat-value">
                            {Number(stats?.average_rating || 0).toFixed(1)}
                            <span className="rm-star">★</span>
                        </p>
                    </div>
                </div>
                
                <div className="rm-stat-card">
                    <div className="rm-stat-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M18 19H6c-1.104 0-2-.896-2-2V9h16v8c0 1.104-.896 2-2 2z"></path>
                            <path d="M5 9h14V7c0-1.105-.895-2-2-2h-2.5l-.5-2h-4l-.5 2H7c-1.105 0-2 .895-2 2v2z"></path>
                        </svg>
                    </div>
                    <div className="rm-stat-content">
                        <h4>Last 30 Days</h4>
                        <p className="rm-stat-value">{stats?.last_30_days || 0}</p>
                    </div>
                </div>

                <div className="rm-stat-card">
                    <div className="rm-stat-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                            <line x1="3" y1="9" x2="21" y2="9"></line>
                            <line x1="9" y1="21" x2="9" y2="9"></line>
                        </svg>
                    </div>
                    <div className="rm-stat-content">
                        <h4>Share Reviews</h4>
                        <button onClick={copyReviewLink} className="rm-button secondary small">
                            Copy Link
                        </button>
                    </div>
                </div>
            </div>

            <div className="rm-question-stats">
                <h3>Question Statistics</h3>
                {stats?.questionStats?.length > 0 ? (
                    <div className="rm-question-stats-grid">
                        {stats.questionStats.map((questionStat) => (
                            <div key={questionStat.question_id} className="rm-question-stat-card">
                                <h4>{questionStat.question_text}</h4>
                                {questionStat.question_type === 'star_rating' ? (
                                    <div className="rm-rating-stats">
                                        <div className="rm-average-rating">
                                            <span className="rm-rating-value">
                                                {Number(questionStat.average_rating || 0).toFixed(1)} ★
                                            </span>
                                            <span className="rm-response-count">
                                                ({questionStat.response_count} responses)
                                            </span>
                                        </div>
                                        {questionStat.rating_distribution && (
                                            <div className="rm-rating-distribution">
                                                {Object.entries(questionStat.rating_distribution)
                                                    .reverse()
                                                    .map(([rating, count]) => (
                                                        <div key={rating} className="rm-rating-bar">
                                                            <span className="rm-rating-label">{rating}★</span>
                                                            <div className="rm-rating-bar-container">
                                                                <div 
                                                                    className="rm-rating-bar-fill"
                                                                    style={{
                                                                        width: `${(Number(count) / Number(questionStat.response_count)) * 100}%`
                                                                    }}
                                                                />
                                                            </div>
                                                            <span className="rm-rating-count">{count}</span>
                                                        </div>
                                                    ))}
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="rm-text-response-stats">
                                        <p>{questionStat.response_count} text responses</p>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="rm-empty-stats">
                        <p>No question statistics available yet. Once customers start submitting reviews, you'll see metrics here.</p>
                    </div>
                )}
            </div>

            <h3>
                Customer Reviews
                {reviews.length > 0 && <span className="rm-review-count">({reviews.length})</span>}
            </h3>
            
            {reviews.length > 0 ? (
                <div className="rm-reviews-list">
                    {reviews.map(review => (
                        <div key={review.id} className="rm-review-card">
                            <div className="rm-review-header">
                                <div className="rm-reviewer-info">
                                    <div className="rm-reviewer-avatar">
                                        {review.reviewer_name ? review.reviewer_name.charAt(0).toUpperCase() : '?'}
                                    </div>
                                    <div className="rm-reviewer-details">
                                        <div className="rm-reviewer-name-container">
                                            <span className="rm-reviewer-name">{review.reviewer_name}</span>
                                            {review.is_registered_user && 
                                                <span className="rm-registered-badge">Registered</span>
                                            }
                                        </div>
                                        {review.reviewer_email && (
                                            <span className="rm-reviewer-email">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                    <polyline points="22,6 12,13 2,6"></polyline>
                                                </svg>
                                                {review.reviewer_email}
                                            </span>
                                        )}
                                        {review.reviewer_phone && (
                                            <span className="rm-reviewer-phone">
                                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                </svg>
                                                {review.reviewer_phone}
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="rm-review-date">
                                    {new Date(review.created_at).toLocaleDateString()}
                                </div>
                            </div>
                            
                            <div className="rm-review-responses">
                                {review.responses_with_questions?.map((response) => (
                                    <div key={response.question_id} className="rm-response-item">
                                        <h5 className="rm-question-text">{response.question_text}</h5>
                                        {response.question_type === 'star_rating' ? (
                                            <div className="rm-stars">
                                                {'★'.repeat(Number(response.answer))}
                                                <span className="rm-stars-empty">{'★'.repeat(5 - Number(response.answer))}</span>
                                            </div>
                                        ) : (
                                            <p className="rm-response-text">"{response.answer}"</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="rm-empty-reviews">
                    <div className="rm-empty-icon">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="48" height="48" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"></path>
                        </svg>
                    </div>
                    <h4>No reviews yet</h4>
                    <p>Share your review link with customers to start collecting feedback</p>
                    <div className="rm-empty-actions">
                        <button onClick={copyReviewLink} className="rm-button secondary">
                            Copy Review Link
                        </button>
                        <button onClick={goToReviewPage} className="rm-button primary">
                            View Review Page
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    if (isLoading) return (
        <div className="rm-loading">
            <div className="rm-spinner"></div>
            <span>Loading review management...</span>
        </div>
    );

    if (error) return (
        <div className="rm-error">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <p>{error}</p>
            <button onClick={() => debouncedFetchData()} className="rm-button primary">
                Try Again
            </button>
        </div>
    );

    return (
        <div className="rm-container">

            {saveStatus.show && (
                            <div className={`rm-toast ${saveStatus.type}`}>
                                <div className="rm-toast-content">
                                    <span className={`rm-toast-icon ${saveStatus.type}`}>
                                        {saveStatus.type === 'success' ? '✓' : '✕'}
                                    </span>
                                    <span className="rm-toast-message">{saveStatus.message}</span>
                                </div>
                            </div>
                        )}


            <div className="rm-header">
                <div className="rm-header-content">
                    <h1>Review Management</h1>
                    <p className="rm-header-subtitle">Collect and manage customer feedback</p>
                </div>
            </div>

            <div className="rm-tab-navigation">
                <button 
                    className={activeSection === 'configuration' ? 'active' : ''}
                    onClick={() => setActiveSection('configuration')}
                >
                    Configuration
                </button>
                <button 
                    className={activeSection === 'reviews' ? 'active' : ''}
                    onClick={() => setActiveSection('reviews')}
                >
                    Reviews
                </button>
            </div>

            <div className="rm-tab-content">
                {activeSection === 'configuration' ? renderConfiguration() : renderReviews()}
            </div>
            {renderConfirmDialog()}
        </div>
    );
};

export default ReviewManagement;